import { BxfChatCanvasWidthEnum } from '@blk/bxf-web-components';
import axios from 'axios';

import { URIS } from '../constants';
import { getCacheValue } from './cache';

export const updateWindowTitle = (displayAppName: string): void => {
    if (displayAppName) {
        document.title = `${displayAppName}`;
    }
};

export const DEV_HOSTNAME = 'https://dev.blackrock.com';
export const getWindow = () => {
    if (typeof window !== 'undefined') {
        return window;
    }
    return undefined;
};

/**
 * Throttles the callback function to prevent it from being called
 * multiple times within a specified time limit.
 * @param callback - The function to throttle
 * @param limit- The time limit in milliseconds
 * @returns {(() => unknown)} A throttled function
 */
export const throttle = (
    callback: () => void,
    limit: number
): (() => unknown) => {
    let wait = false;
    return () => {
        if (!wait) {
            callback();
            wait = true;
            setTimeout(function () {
                wait = false;
            }, limit);
        }
    };
};

export const getApiBaseRef = (): string =>
    getWindow()?.location?.origin || DEV_HOSTNAME;

export const getAxiosInstance = (baseURL: string, extraConfig = {}) =>
    axios.create({
        baseURL,
        ...extraConfig,
    });

export const getHost = (isLocalhost = false): string => {
    if (isLocalhost) {
        return '';
    }

    const url = getStorageConfigValue('issuer');
    if (url) {
        return url.startsWith('http') ? url : `https://${url}`;
    } else {
        let path = 'https://test-connect.blackrock.com';
        if (URIS.PROD_HOSTS.includes(window.location.hostname)) {
            path = 'https://connect.blackrock.com';
        }
        return path;
    }
};

export const getStorageConfigValue = (key = ''): string => {
    const prefix = 'chatblk_';
    const config = getCacheValue('config', true) as object;
    const ind = key.startsWith(prefix) ? key : `${prefix}${key}`;
    return config?.[ind] || '';
};

export const saveUserData = (userId, key, data) => {
    const storageKey = `user_${userId}_${key}`;
    localStorage.setItem(storageKey, JSON.stringify(data));
};

export const getUserData = (userId, key) => {
    const storageKey = `user_${userId}_${key}`;
    const storedData = localStorage.getItem(storageKey);
    return storedData ? JSON.parse(storedData) : null;
};

export const removeUserData = (userId, key) => {
    const storageKey = `user_${userId}_${key}`;
    localStorage.removeItem(storageKey);
};

export const getCanvasWidth = (viewportWidth: number): number => {
    const { XS, LMS, INITIAL } = BxfChatCanvasWidthEnum;
    if (viewportWidth > 1440) {
        return INITIAL;
    } else if (viewportWidth > 599) {
        return LMS;
    }
    return XS;
};

export const getKey = (prefix: string, index: number | string) => {
    const suffix =
        typeof index === 'number' ? `${index}`.padStart(2, '0') : `${index}`;
    return `${prefix}-${suffix}`;
};

/**
 * Computes the visible rectangle of an HTML element by intersecting its bounding rectangle with
 * the clipping regions of its ancestors and the viewport.
 *
 * The function iterates through the element's ancestors, checking for CSS overflow properties
 * that cause clipping (e.g., "hidden", "scroll", or "auto"). It calculates the intersection
 * of the element's bounding rectangle with each ancestor's bounding rectangle. Finally, it
 * intersects the result with the viewport bounds. If the element is fully clipped by any
 * clipping region or lies completely outside of the viewport, the function returns null.
 *
 * @param el - The HTML element whose visible rectangle is to be calculated.
 * @returns A DOMRect representing the visible portion of the element, or null if the element is fully clipped.
 */
export function getVisibleRect(el: HTMLElement): DOMRect | null {
    if (!el) return null;
    let rect = el.getBoundingClientRect();

    // Intersect with all ancestors that clip overflow
    let ancestor = el.parentElement;
    while (ancestor) {
        const style = window.getComputedStyle(ancestor);

        // Only clip if ancestor can restrict overflow
        if (
            /(hidden|scroll|auto)/.test(
                style.overflow + style.overflowY + style.overflowX
            )
        ) {
            const aRect = ancestor.getBoundingClientRect();
            // Calculate intersection
            const left = Math.max(rect.left, aRect.left);
            const right = Math.min(rect.right, aRect.right);
            const top = Math.max(rect.top, aRect.top);
            const bottom = Math.min(rect.bottom, aRect.bottom);
            if (right <= left || bottom <= top) return null; // Fully clipped
            rect = new DOMRect(left, top, right - left, bottom - top);
        }
        ancestor = ancestor.parentElement;
    }

    // Intersect with viewport
    const left = Math.max(rect.left, 0);
    const right = Math.min(rect.right, window.innerWidth);
    const top = Math.max(rect.top, 0);
    const bottom = Math.min(rect.bottom, window.innerHeight);
    if (right <= left || bottom <= top) return null; // Fully clipped
    return new DOMRect(left, top, right - left, bottom - top);
}

export function getLowestVisiblePoint(el: HTMLElement): number | null {
    const visibleRect = getVisibleRect(el);
    if (!visibleRect) return null;

    // Check children recursively
    const children = Array.from(el.children) as HTMLElement[];
    let lowest = visibleRect.bottom;

    children.forEach((child) => {
        const childLowest = getLowestVisiblePoint(child);
        if (childLowest !== null) {
            lowest = Math.max(lowest, childLowest);
        }
    });

    return lowest;
}
